import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Upload, Send, Plus, Trash2, Calendar, FileText,
  User, Mail, Clock, AlertCircle, ArrowRight, BookTemplate, X, FileCheck, ChevronDown, ChevronUp, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import RecipientRoleSelect from '../components/RecipientRoleSelect';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const SendDoc = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { API, accessToken } = useContext(AuthContext);
  
  const [documentFile, setDocumentFile] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [fields, setFields] = useState([]);
  const [recipients, setRecipients] = useState([
    { name: '', email: '', role: 'Signer' }
  ]);
  const [expiryDays, setExpiryDays] = useState(30);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(600);
  const containerRef = useRef(null);
  const [existingEnvelopeId, setExistingEnvelopeId] = useState(null);

  // Recipient section state
  const [isRecipientSectionExpanded, setIsRecipientSectionExpanded] = useState(true);
  const [recipientErrors, setRecipientErrors] = useState({}); // { [index]: { name: string, email: string } }
  const recipientSectionRef = useRef(null);

  // Recipient count modal state (for Send a Doc flow only)
  const [showRecipientCountModal, setShowRecipientCountModal] = useState(false);
  const [recipientCount, setRecipientCount] = useState(1);
  const [isTemplateMode, setIsTemplateMode] = useState(false);

  // Check if returning from field editor
  useEffect(() => {
    if (location.state) {
      const { fields: returnedFields, fileId, documentTitle: title, envelopeId, recipients: returnedRecipients } = location.state;
      if (returnedFields) {
        setFields(returnedFields);
        setUploadedFileId(fileId);
        setDocumentTitle(title);
        if (envelopeId) {
          setExistingEnvelopeId(envelopeId);
        }
        // Restore recipients from field editor
        if (returnedRecipients && returnedRecipients.length > 0) {
          setRecipients(returnedRecipients);
        }
        toast.success(`${returnedFields.length} fields placed! Now add recipients.`);

        // Load PDF for preview
        if (fileId) {
          loadPdfForPreview(fileId);
        }
      }
    }
  }, [location.state]);
  
  // Load PDF for preview
  const loadPdfForPreview = async (fileId) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: 'arraybuffer'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Failed to load PDF for preview:', error);
    }
  };
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  
  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);
  
  // Update page width when container size changes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setPageWidth(width - 40);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const SUPPORTED_FORMATS = [
    '.pdf',       // PDF files
    '.doc',       // Word 97-2003
    '.docx'       // Word 2007+
  ];

  const MAX_PAGES = 100; // Maximum allowed pages

  // Show recipient count modal before navigating to field editor
  const handleContinueToRecipients = () => {
    if (!documentFile) {
      toast.error('Please select a document');
      return;
    }
    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    // Show recipient count modal (Send a Doc flow only, not templates)
    setShowRecipientCountModal(true);
  };

  // New flow: recipients are filled first, then go directly to field editor
  const handleContinueToFields = async () => {
    if (!documentFile) {
      toast.error('Please select a document');
      return;
    }
    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    const hasValid = recipients.some(r => r.name?.trim() && r.email?.trim());
    if (!hasValid) {
      toast.error('Please add at least one recipient with name and email');
      return;
    }
    // Attach signerIndex / signerId for field assignment, then go to editor
    const enrichedRecipients = recipients.map((r, idx) => ({
      ...r,
      signerIndex: idx + 1,
      signerId: r.signerId || `signer_${idx + 1}`
    }));
    setRecipients(enrichedRecipients);
    await handleUploadAndPlaceFields(enrichedRecipients);
  };

  // Handle recipient count confirmation and proceed to upload
  const handleRecipientCountConfirm = async () => {
    // Set up recipients based on count
    // Use placeholder identifiers (signer_1, signer_2, etc.) for field assignment
    // These will be mapped to actual emails when sending
    const newRecipients = [];
    for (let i = 0; i < recipientCount; i++) {
      newRecipients.push({
        name: recipients[i]?.name || '',
        email: recipients[i]?.email || '',
        role: 'Signer',
        signerIndex: i + 1, // Track signer index for field assignment mapping
        signerId: `signer_${i + 1}` // Placeholder ID for field assignment
      });
    }
    setRecipients(newRecipients);
    setShowRecipientCountModal(false);

    // Proceed to upload and field placement
    await handleUploadAndPlaceFields(newRecipients);
  };

  const handleUploadAndPlaceFields = async (recipientsToUse = recipients) => {
    if (!documentFile) {
      toast.error('Please select a document');
      return;
    }
    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    try {
      setIsSending(true);
      setUploadProgress(10);

      // Validate PDF page count before uploading (only for PDFs)
      if (documentFile.type === 'application/pdf') {
        try {
          const pdfjsLib = window['pdfjs-dist/build/pdf'];
          if (pdfjsLib) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

            const arrayBuffer = await documentFile.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            const pageCount = pdf.numPages;

            if (pageCount > MAX_PAGES) {
              toast.error(`Document has ${pageCount} pages. Maximum allowed is ${MAX_PAGES} pages. Please use a shorter document.`);
              setIsSending(false);
              setUploadProgress(0);
              return;
            }
          }
        } catch (pdfError) {
          console.warn('PDF validation skipped:', pdfError);
          // Continue with upload even if validation fails
        }

        setUploadProgress(30);
      }

      // Step 1: Create envelope early (Draft status)
      const envelopeResponse = await axios.post(
        `${API}/envelopes`,
        {
          title: documentTitle,
          recipients: [],  // Empty recipients for draft
          variables: {}
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      const envelopeId = envelopeResponse.data.id;

      setUploadProgress(50);

      // Step 2: Upload file
      const formData = new FormData();
      formData.append('file', documentFile);

      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      const fileResponse = await axios.post(`${API}/files/upload`, formData, { headers });
      const fileId = fileResponse.data.file_id;

      setUploadProgress(70);

      // Step 3: Add document to envelope
      const docResponse = await axios.post(
        `${API}/envelopes/${envelopeId}/documents`,
        {
          file_id: fileId,
          order_index: 1,
          source: 'upload'
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      const docId = docResponse.data.id;

      setUploadProgress(100);

      toast.success('Document uploaded! Opening field editor...');

      // Navigate to field editor with envelope ID and docId
      // Filter signers only (not CC) for field assignment
      const signers = recipientsToUse.filter(r => r.role === 'Signer');
      setTimeout(() => {
        navigate('/send/editor', {
          state: {
            envelopeId,
            docId,  // Pass the envelope_docs ID
            fileId,
            fileName: documentFile.name,
            documentTitle,
            recipients: recipientsToUse.filter(r => r.name || r.email), // Pass recipient data (even partial)
            signerCount: signers.length, // Number of signers for field assignment
            expiryDays,  // Pass expiry setting so editor can stamp it before send
            // Pass signers with signerId for field assignment (use signerId if available, otherwise generate)
            signers: signers.map((s, idx) => ({
              ...s,
              signerIndex: idx + 1,
              signerId: s.signerId || `signer_${idx + 1}` // Use consistent signer ID for field assignment
            }))
          }
        });
      }, 500);

    } catch (error) {
      toast.error('Failed to upload document');
      console.error(error);
      setUploadProgress(0);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }

      setDocumentFile(file);

      // Clear any previous upload (e.g. after Back from the field editor) so the new file starts fresh
      if (uploadedFileId || existingEnvelopeId || fields.length > 0) {
        setUploadedFileId(null);
        setExistingEnvelopeId(null);
        setFields([]);
        setIsTemplateMode(false);
        setPdfUrl(null);
        setNumPages(null);
        setPageNumber(1);
        if (location.state) {
          navigate(location.pathname, { replace: true, state: null });
        }
      }

      // Auto-set title from filename if not set
      if (!documentTitle) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setDocumentTitle(nameWithoutExt);
      }
      
      toast.success(`File selected: ${file.name}`);
    }
  };

  // Template management functions
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      // Filter only active templates
      const activeTemplates = response.data.filter(t => t.status === 'active');
      setTemplates(activeTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const useTemplate = async (templateId) => {
    try {
      toast.info('Loading template...');
      
      // Get template details including fields and document
      const response = await axios.get(`${API}/templates/${templateId}/details`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      const { template, version, fields, document } = response.data;

      // Debug: Log template fields to see structure
      console.log('[TEMPLATE] Loaded fields from template:', fields);
      fields.forEach((field, idx) => {
        if (field.type === 'radio' || field.type === 'checkbox-group') {
          console.log(`[TEMPLATE] Field ${idx} (${field.type}):`, {
            props: field.props,
            options: field.options,
            'props.options': field.props?.options
          });
        }
        if (field.type === 'date') {
          console.log(`📅 [TEMPLATE] Loaded date field ${idx}:`, {
            id: field.id,
            type: field.type,
            props: field.props,
            'props.format': field.props?.format,
            fullField: field
          });
        }
      });

      // Set document title from template
      setDocumentTitle(template.name);

      // Set the file ID from template document
      setUploadedFileId(document.id);

      // Convert template fields to envelope fields format
      const convertedFields = fields.map(field => {
        // For radio/checkbox-group, ensure props.options is preserved
        let props = field.props || {};
        if ((field.type === 'radio' || field.type === 'checkbox-group') && !props.options && field.options) {
          props = { ...props, options: field.options };
        }

        const converted = {
          id: field.id,
          type: field.type,
          role: 'Signer',  // Default role for template fields
          x: field.x || 0,
          y: field.y || 0,
          width: field.width || 150,
          height: field.height || 30,
          page: field.page || 1,
          props: props,
          label: field.label || field.type
        };

        // Debug logging for radio/checkbox-group and date fields
        if (field.type === 'radio' || field.type === 'checkbox-group') {
          console.log(`[TEMPLATE] Converted field ${field.id} (${field.type}):`, converted);
        }
        if (field.type === 'date') {
          console.log(`📅 [TEMPLATE] Date field ${field.id}:`, {
            'field.props': field.props,
            'field.props?.format': field.props?.format,
            'converted.props': converted.props,
            'converted.props.format': converted.props?.format
          });
        }

        return converted;
      });
      
      setFields(convertedFields);

      // Mark as template mode (disables recipient count prompt, allows only 1 signer)
      setIsTemplateMode(true);

      // Close modal
      setShowTemplateModal(false);

      toast.success(`Template "${template.name}" loaded with ${fields.length} fields`);
    } catch (error) {
      console.error('Failed to use template:', error);
      toast.error('Failed to load template');
    }
  };

  const openTemplateModal = () => {
    setShowTemplateModal(true);
    loadTemplates();
  };

  // Recipient management functions

  const handleAddRecipient = () => {
    setRecipients([...recipients, { name: '', email: '', role: 'Signer' }]);
  };

  const handleRemoveRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const handleRecipientChange = (index, field, value) => {
    const newRecipients = [...recipients];
    
    // Check for duplicate email when email field is changed
    if (field === 'email' && value.trim()) {
      const isDuplicate = recipients.some((recipient, i) => 
        i !== index && recipient.email.toLowerCase() === value.toLowerCase()
      );
      
      if (isDuplicate) {
        toast.error('This email address is already added as a recipient');
        return; // Don't update if duplicate
      }
    }
    
    newRecipients[index][field] = value;
    setRecipients(newRecipients);
  };

  // Validate recipients and return errors object
  const validateRecipients = () => {
    const errors = {};
    let hasErrors = false;

    // Check if at least one valid recipient exists
    const hasValidRecipient = recipients.some(r => r.name.trim() && r.email.trim());

    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      const recipientErrors = {};

      if (!recipient.name.trim()) {
        recipientErrors.name = 'Name is required';
        hasErrors = true;
      }
      if (!recipient.email.trim()) {
        recipientErrors.email = 'Email is required';
        hasErrors = true;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email)) {
        recipientErrors.email = 'Invalid email format';
        hasErrors = true;
      }

      // Check for duplicate emails
      const emailLower = recipient.email.toLowerCase();
      const duplicateIndex = recipients.findIndex((r, idx) =>
        idx < i && r.email.toLowerCase() === emailLower
      );
      if (duplicateIndex !== -1 && recipient.email.trim()) {
        recipientErrors.email = 'Duplicate email address';
        hasErrors = true;
      }

      if (Object.keys(recipientErrors).length > 0) {
        errors[i] = recipientErrors;
      }
    }

    return { errors, hasErrors, hasValidRecipient };
  };

  const validateForm = () => {
    // Check if document exists (either uploaded file or template file ID)
    if (!documentFile && !uploadedFileId) {
      toast.error('Please select a document or use a template');
      return false;
    }

    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return false;
    }

    // Check if fields exist when using template
    if (uploadedFileId && (!fields || fields.length === 0)) {
      toast.error('Template has no fields configured. Please add fields before sending.');
      return false;
    }

    // Validate recipients with detailed errors
    const { errors, hasErrors, hasValidRecipient } = validateRecipients();

    if (!hasValidRecipient || hasErrors) {
      setRecipientErrors(errors);
      // Auto-expand recipient section and scroll to it
      setIsRecipientSectionExpanded(true);
      setTimeout(() => {
        if (recipientSectionRef.current) {
          recipientSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Show specific error message
      if (!hasValidRecipient) {
        toast.error('Please add at least one recipient with name and email');
      } else {
        // Find first error to display
        for (let i = 0; i < recipients.length; i++) {
          if (errors[i]) {
            if (errors[i].name) {
              toast.error(`Recipient ${i + 1}: ${errors[i].name}`);
              break;
            }
            if (errors[i].email) {
              toast.error(`Recipient ${i + 1}: ${errors[i].email}`);
              break;
            }
          }
        }
      }
      return false;
    }

    // Clear any previous errors
    setRecipientErrors({});
    return true;
  };

  const handleSendDocument = async () => {
    if (!validateForm()) return;

    try {
      setIsSending(true);
      setUploadProgress(10);

      let envelopeIdToUse = existingEnvelopeId;

      // If we have an existing envelope (from field editor), just add recipients and send
      if (existingEnvelopeId) {
        // CRITICAL: Map signer IDs to actual emails in field assignedTo before sending
        // Fields were assigned to "signer_1", "signer_2", etc. during field placement
        // Now we need to update them with actual recipient emails
        setUploadProgress(20);

        // Create signer ID to email mapping
        const signerEmailMap = {};
        recipients.filter(r => r.role === 'Signer').forEach((r, idx) => {
          const signerId = r.signerId || `signer_${idx + 1}`;
          signerEmailMap[signerId] = r.email;
        });

        console.log('📧 Signer ID to email mapping:', signerEmailMap);

        // Update fields with actual recipient emails
        if (fields && fields.length > 0) {
          const updatedFields = fields.map(field => {
            const assignedTo = field.props?.assignedTo || '';
            const actualEmail = signerEmailMap[assignedTo] || assignedTo;

            console.log(`📝 Field ${field.id}: assignedTo "${assignedTo}" -> "${actualEmail}"`);

            return {
              ...field,
              props: {
                ...field.props,
                assignedTo: actualEmail
              }
            };
          });

          // Save updated fields with actual emails
          setUploadProgress(30);
          await axios.post(
            `${API}/envelopes/${existingEnvelopeId}/fields`,
            { fields: updatedFields },
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );

          console.log('✅ Fields updated with actual recipient emails');
        }

        // Add recipients to existing envelope
        setUploadProgress(50);
        await axios.post(
          `${API}/envelopes/${existingEnvelopeId}/recipients`,
          {
            recipients: recipients.map((r, index) => ({
              name: r.name,
              email: r.email,
              role: r.role,
              signing_order: index + 1
            }))
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        // Send the envelope
        setUploadProgress(70);
        await axios.post(
          `${API}/envelopes/${existingEnvelopeId}/send`,
          { message: message || undefined },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } else {
        // Create new envelope and send in one go (original flow)
        // Map signer IDs to actual emails in fields before sending
        const signerEmailMap = {};
        recipients.filter(r => r.role === 'Signer').forEach((r, idx) => {
          const signerId = r.signerId || `signer_${idx + 1}`;
          signerEmailMap[signerId] = r.email;
        });

        const mappedFields = fields.map(field => {
          const assignedTo = field.props?.assignedTo || '';
          const actualEmail = signerEmailMap[assignedTo] || assignedTo;
          return {
            ...field,
            props: {
              ...field.props,
              assignedTo: actualEmail
            }
          };
        });

        const envelopeData = {
          title: documentTitle,
          recipients: recipients.map((r, index) => ({
            name: r.name,
            email: r.email,
            role: r.role,
            signing_order: index + 1
          })),
          file_id: uploadedFileId,
          fields: mappedFields,
          message: message || undefined,
          expiry_days: expiryDays
        };

        setUploadProgress(70);
        const envelopeResponse = await axios.post(`${API}/envelopes/send-doc`, envelopeData, {
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
        });

        envelopeIdToUse = envelopeResponse.data.envelope_id;
      }
      
      setUploadProgress(100);
      toast.success('Document sent successfully!');
      
      // Navigate to envelope detail
      setTimeout(() => {
        navigate(`/envelopes/${envelopeIdToUse}`);
      }, 1000);

    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send document');
      setUploadProgress(0);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <Send size={28} className="text-indigo-600 sm:hidden" />
            <Send size={40} className="text-indigo-600 hidden sm:inline" />
            Send a Document
          </h1>
          <p className="text-gray-600 mt-2">Upload and send documents for signature without creating templates</p>
        </div>

        {/* ── Flow Stepper ───────────────────────────────────────────────── */}
        {(() => {
          const hasValidRecipient = recipients.some(r => r.name?.trim() && r.email?.trim());
          const step1Done = !!(documentFile && documentTitle.trim());
          const step2Done = hasValidRecipient;
          const step3Done = !!(uploadedFileId && fields.length > 0);
          const steps = [
            { label: 'Upload', done: step1Done, active: !step1Done },
            { label: 'Recipients', done: step2Done, active: step1Done && !step2Done },
            { label: 'Select Fields', done: step3Done, active: step2Done && !step3Done },
            { label: 'Send', done: false, active: step3Done },
          ];
          return (
            <div className="flex items-center gap-0">
              {steps.map((s, i) => (
                <React.Fragment key={s.label}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                      s.done
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : s.active
                        ? 'bg-white border-indigo-600 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}>
                      {s.done ? '✓' : i + 1}
                    </div>
                    <span className={`text-xs mt-1 font-medium ${
                      s.done ? 'text-indigo-600' : s.active ? 'text-indigo-600' : 'text-gray-400'
                    }`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-4 mx-1 transition-colors ${
                      steps[i + 1].done || (s.done && steps[i + 1].active) ? 'bg-indigo-600' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          );
        })()}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Document Upload */}
          <div className="md:col-span-2 space-y-6">
            {/* Document Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Upload size={20} />
                    Upload Document
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openTemplateModal}
                    className="flex items-center gap-2"
                  >
                    <BookTemplate size={16} />
                    Use Template
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Document Title *</label>
                  <Input
                    type="text"
                    placeholder="e.g., Employment Contract, NDA, Agreement"
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Select File *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors">
                    <input
                      type="file"
                      accept={SUPPORTED_FORMATS.join(',')}
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600 font-medium mb-2">
                        {documentFile ? (
                          <span className="text-green-600 flex items-center justify-center gap-2">
                            <FileText size={20} />
                            {documentFile.name}
                            <span className="text-xs text-gray-500">
                              ({(documentFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </span>
                        ) : (
                          'Click to upload or drag and drop'
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Supported: PDF and Word (.doc, .docx) files only
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Max file size: 50MB | Max pages: 100</p>
                    </label>
                  </div>
                </div>

                {/* Supported Formats */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-800">
                      <p className="font-semibold mb-1">Supported Formats:</p>
                      <p>• PDF (.pdf)<br/>
                         • Microsoft Word (.doc, .docx)<br/>
                         <span className="text-blue-600 font-medium">Word files will be automatically converted to PDF for signing</span></p>
                    </div>
                  </div>
                </div>



                {/* Document Uploaded */}
                {uploadedFileId && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-800 mb-1">
                      ✓ Document uploaded successfully!
                    </p>
                    <p className="text-xs text-green-700">
                      {fields.length > 0 
                        ? `${fields.length} fields placed. Add recipients below to send.`
                        : 'Signature field will be auto-placed. Add recipients below to send.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recipients Section */}
            <Card ref={recipientSectionRef} className={Object.keys(recipientErrors).length > 0 ? 'border-red-300 ring-1 ring-red-200' : ''}>
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsRecipientSectionExpanded(!isRecipientSectionExpanded)}
              >
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User size={20} />
                    Recipients ({recipients.length})
                    {Object.keys(recipientErrors).length > 0 && (
                      <span className="flex items-center gap-1 text-red-600 text-sm font-normal">
                        <AlertCircle size={16} />
                        Validation errors
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleAddRecipient(); }}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600"
                    >
                      <Plus size={16} className="mr-1" />
                      Add
                    </Button>
                    {isRecipientSectionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </CardTitle>
              </CardHeader>
              {isRecipientSectionExpanded && (
                <CardContent className="space-y-4">
                  {recipients.map((recipient, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 space-y-3 ${
                        recipientErrors[index] ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          Recipient {index + 1}
                          {recipient.role === 'Signer' && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                              Signing Order: {recipients.filter((r, i) => i <= index && r.role === 'Signer').length}
                            </span>
                          )}
                        </span>
                        {recipients.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRecipient(index)}
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-1 block">Name *</label>
                        <Input
                          type="text"
                          placeholder="John Doe"
                          value={recipient.name}
                          onChange={(e) => {
                            handleRecipientChange(index, 'name', e.target.value);
                            // Clear error on change
                            if (recipientErrors[index]?.name) {
                              setRecipientErrors(prev => {
                                const updated = { ...prev };
                                if (updated[index]) {
                                  delete updated[index].name;
                                  if (Object.keys(updated[index]).length === 0) delete updated[index];
                                }
                                return updated;
                              });
                            }
                          }}
                          className={recipientErrors[index]?.name ? 'border-red-500' : ''}
                          required
                        />
                        {recipientErrors[index]?.name && (
                          <p className="text-xs text-red-600 mt-1">{recipientErrors[index].name}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-1 block">Email *</label>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          value={recipient.email}
                          onChange={(e) => {
                            handleRecipientChange(index, 'email', e.target.value);
                            // Clear error on change
                            if (recipientErrors[index]?.email) {
                              setRecipientErrors(prev => {
                                const updated = { ...prev };
                                if (updated[index]) {
                                  delete updated[index].email;
                                  if (Object.keys(updated[index]).length === 0) delete updated[index];
                                }
                                return updated;
                              });
                            }
                          }}
                          className={recipientErrors[index]?.email ? 'border-red-500' : ''}
                          required
                        />
                        {recipientErrors[index]?.email && (
                          <p className="text-xs text-red-600 mt-1">{recipientErrors[index].email}</p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-1 block">Role</label>
                        <RecipientRoleSelect
                          value={recipient.role}
                          onChange={(newRole) => handleRecipientChange(index, 'role', newRole)}
                          isTemplateFlow={isTemplateMode}
                          recipients={recipients}
                          currentIndex={index}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>

            {/* ── Continue to Select Fields ────────────────────────────── */}
            {!uploadedFileId && (
              (() => {
                const hasValidRecipient = recipients.some(r => r.name?.trim() && r.email?.trim());
                const canProceed = !!(documentFile && documentTitle.trim() && hasValidRecipient);
                return (
                  <div>
                    <Button
                      onClick={handleContinueToFields}
                      disabled={!canProceed || isSending}
                      className={`w-full h-12 text-base font-semibold transition-all ${
                        canProceed
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                      }`}
                    >
                      {isSending ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Uploading… {uploadProgress}%
                        </>
                      ) : (
                        <>
                          <ArrowRight size={20} className="mr-2" />
                          Continue to Select Fields
                        </>
                      )}
                    </Button>
                    {!canProceed && (
                      <p className="text-xs text-gray-400 mt-2 text-center">
                        {!documentFile || !documentTitle.trim()
                          ? 'Upload a document and add a title first'
                          : 'Add at least one recipient with name and email to continue'}
                      </p>
                    )}
                  </div>
                );
              })()
            )}

            {/* Message */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail size={20} />
                  Email Message (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md"
                  rows="4"
                  placeholder="Add a personal message to recipients..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Settings & Send */}
          <div className="space-y-6">
            {/* Expiry Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock size={20} />
                  Expiry Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Document expires in:</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={expiryDays}
                      onChange={(e) => setExpiryDays(parseInt(e.target.value) || 30)}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-600">days</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    After this period, the signing link will expire
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Calendar size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-yellow-800">
                      <p className="font-semibold mb-1">Expiry Date:</p>
                      <p>{new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Document:</span>
                  <span className="font-medium">
                    {documentFile ? documentFile.name.substring(0, 20) + '...' : 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipients:</span>
                  <span className="font-medium">{recipients.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expires in:</span>
                  <span className="font-medium">{expiryDays} days</span>
                </div>
              </CardContent>
            </Card>

            {/* Send Button - Show after fields are placed OR when using template */}
            {(fields.length > 0 || uploadedFileId) && (
              <Button
                onClick={handleSendDocument}
                disabled={isSending || (!documentFile && !uploadedFileId) || !documentTitle}
                className="w-full h-14 text-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                {isSending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Send for Signature
                  </>
                )}
              </Button>
            )}

            {/* Help Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">Quick Send</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Upload any supported document format</li>
                <li>• Add recipient details</li>
                <li>• Set expiry date</li>
                <li>• Recipients receive email with signing link</li>
                <li>• No template creation needed!</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Recipient Count Modal - Send a Doc flow only */}
      {showRecipientCountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Users size={24} className="text-indigo-600" />
                  How many signers?
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select the number of people who need to sign this document
                </p>
              </div>
              <button
                onClick={() => setShowRecipientCountModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="grid grid-cols-5 gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((count) => (
                  <button
                    key={count}
                    onClick={() => setRecipientCount(count)}
                    className={`p-4 rounded-lg border-2 text-lg font-semibold transition-all ${
                      recipientCount === count
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-indigo-300 text-gray-700'
                    }`}
                  >
                    {count}
                  </button>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Signing order:</strong> Each signer will receive the document in sequence.
                  Signer 1 signs first, then Signer 2, and so on.
                </p>
              </div>

              {recipientCount > 1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-yellow-800">
                    <strong>Multiple signers:</strong> You'll be able to assign fields to specific signers
                    in the field editor. Each signer can only interact with their assigned fields.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowRecipientCountModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
                  onClick={handleRecipientCountConfirm}
                  disabled={isSending}
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ArrowRight size={18} className="mr-2" />
                      Continue to Place Fields
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Select a Template</h2>
                <p className="text-sm text-gray-500 mt-1">Choose from your published templates</p>
              </div>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <BookTemplate size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium mb-2">No Templates Found</p>
                  <p className="text-sm text-gray-500">
                    Create and publish templates first to use them here
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => useTemplate(template.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-1">
                            {template.name}
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(template.created_at).toLocaleDateString()}
                            </span>
                            {template.tags && template.tags.length > 0 && (
                              <span className="flex items-center gap-1">
                                {template.tags.slice(0, 2).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            useTemplate(template.id);
                          }}
                          className="ml-4"
                        >
                          Use This
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SendDoc;
